import React, { useState } from 'react';
import { TitledSet } from '../../components/nucleus/SetHead';
import Switch from '../../components/nucleus/Switch';
import { PluginFace } from '../../components/nucleus/faces';
import { useConfirm } from '../../components/nucleus/actions';
import { useToast } from '../../components/common';
import { ago } from '../../nucleus/format';
import { pluginsApi } from './data/api';
import { toolsOf } from './data/items';
import { pluginNote } from './data/lists';
import { howItRuns, needWord, stuckWord, turnOffAsk } from './data/words';
import './plugin-tree.css';

// MY PLUGINS, AS ONE LIST: each plugin a row (its face, its name, how it
// stands, and at its end Continue while it is Setting up or its On/Off switch),
// its tools indented under it on a hairline guide (each with how it runs, when
// it last ran, and Run). Sets: Setting up, Ready, Off. A tool row opens the
// tool's sheet: its Run tab, or its Runs automatically tab from "how it runs". A tool of
// a plugin still Setting up is muted and has no Run.
//
//   <PluginTree sets={[{ id, title, list, empty }]} query openId groupId gate runHint
//       onOpen(id) onOpenTool(pluginId, toolId, tab) onContinue(item) onChanged />

const TROUBLED = ['failing', 'paused', 'needs_realm'];
const has = (s, q) => String(s || '').toLowerCase().includes(q);

/** How one tool stands on its row: { how, tone } — what runs it, or why it can't run. */
export function toolLine(item, tool) {
    const s = tool.schedule || { state: 'off' };
    // Off runs nothing on its own; Setting up says only what it will do
    if (item.paused) return { how: howItRuns({ state: 'off' }), tone: null };
    if (item.settingUp || !item.installed) return { how: howItRuns(s), tone: null };
    if (TROUBLED.includes(s.state)) return { how: stuckWord({ ...s, tool: tool.name }, { named: false }), tone: s.state === 'failing' ? 'live' : 'warn' };
    const need = (tool.needs || []).find((n) => n.kind !== 'install');
    if (!tool.runnable && need) return { how: need.kind === 'realm' ? `Can’t run: ${needWord(need).toLowerCase()}` : needWord(need), tone: 'warn' };
    return { how: howItRuns(s), tone: null };
}

/** The tools a find leaves showing: all of them when the plugin's name matches. */
export function toolsShown(item, tools, query) {
    const q = String(query || '').trim().toLowerCase();
    if (!q || has(item.name, q)) return tools;
    return tools.filter((t) => has(t.name, q));
}

/** A plugin's On/Off: the one switch. Turning it off asks first. */
export function PluginPower({ item, groupId, gate, onChanged }) {
    const confirm = useConfirm();
    const toast = useToast();
    const [busy, setBusy] = useState(false);
    const on = !item.paused;
    const flip = async (next) => {
        if (!next && !(await confirm(turnOffAsk(item.name)))) return;
        setBusy(true);
        try {
            await (next ? pluginsApi.resume : pluginsApi.pause)(item.id, groupId);
            toast.success(`${item.name} is ${next ? 'on' : 'off'}.`);
            onChanged?.();
        } catch (e) {
            toast.error(e?.message || `Could not turn ${item.name} ${next ? 'on' : 'off'}.`);
        } finally {
            setBusy(false);
        }
    };
    return (
        <span className="plugins-tree__power" title={gate.isAdmin ? undefined : gate.hint}>
            <span className="plugins-tree__power-word" aria-hidden="true">{on ? 'On' : 'Off'}</span>
            <Switch aria-label={`${item.name} on`} checked={on} disabled={busy || !gate.isAdmin} onChange={(e) => flip(e.target.checked)} />
        </span>
    );
}

/** One tool's row: its name, how it runs (or why it can't), when it last ran, and Run. */
export function ToolRow({ item, tool, runHint, onOpenTool }) {
    const muted = !item.installed || item.settingUp;
    const s = tool.schedule || {};
    const { how, tone } = toolLine(item, tool);
    const last = s.last_run_at ? `last ${ago(s.last_run_at)}` : null;
    const open = (tab) => onOpenTool?.(item.id, tool.id, tab);
    return (
        <li className={`plugins-tree__tool${muted ? ' is-muted' : ''}`}>
            <button type="button" className="plugins-tree__name" onClick={() => open('run')}>
                <span className="plugins-tree__word">{tool.name}</span>
            </button>
            <button type="button" className="plugins-tree__how" onClick={() => open('schedule')} aria-label={`${tool.name}: ${how}${last ? `, ${last}` : ''}`}>
                <span className={`plugins-tree__runs${tone ? ` is-${tone}` : ''}`}>{how}</span>
                <span className={`plugins-tree__last${s.last_status === 'failed' ? ' is-live' : ''}`}>{last}</span>
            </button>
            <span className="plugins-tree__end">
                {!muted && (
                    <span className="plugins-tree__cell" title={runHint || undefined}>
                        <button type="button" className="plugins-tree__run" aria-label={`Run ${tool.name}`} disabled={!!runHint} onClick={() => open('run')}>Run</button>
                    </span>
                )}
            </span>
        </li>
    );
}

/** A plugin's row: its face, name and how it stands; Continue or its switch at the end. */
function PluginRow({ item, current, gate, groupId, heldId, onOpen, onContinue, onChanged }) {
    const note = pluginNote(item);
    return (
        <div className={`plugins-tree__plugin${current ? ' is-current' : ''}`}>
            <button type="button" className="plugins-tree__open" aria-current={current ? 'true' : undefined} onClick={() => onOpen(item.id)}>
                <span className="plugins-tree__head">
                    <span className="plugins-tree__face" aria-hidden="true"><PluginFace size={24} logo={item.logo} /></span>
                    <span className="plugins-tree__word">{item.name}</span>
                </span>
                <span className={`plugins-tree__state${item.settingUp ? ' is-warn' : item.paused ? ' is-quiet' : ''}`}>{note}</span>
            </button>
            <span className="plugins-tree__end">
                {item.settingUp && (
                    <span className="plugins-tree__cell" title={gate.isAdmin ? undefined : gate.hint}>
                        <button type="button" className="plugins-tree__continue" disabled={!gate.isAdmin}
                            aria-describedby={!gate.isAdmin && heldId ? heldId : undefined}
                            aria-label={`Continue setting up ${item.name}`} onClick={() => onContinue(item)}>
                            Continue
                        </button>
                    </span>
                )}
                {!item.settingUp && item.installed && <PluginPower item={item} groupId={groupId} gate={gate} onChanged={onChanged} />}
            </span>
        </div>
    );
}

/** One plugin and its tools, indented: what both My plugins and the inspector's Tools band draw. */
export function PluginBranch({ item, query, current, gate, groupId, runHint, heldId, onOpen, onOpenTool, onContinue, onChanged, tools: given }) {
    const tools = toolsShown(item, given || toolsOf(item), query);
    return (
        <li className="plugins-tree__branch">
            <PluginRow item={item} current={current} gate={gate} groupId={groupId} heldId={heldId}
                onOpen={onOpen} onContinue={onContinue} onChanged={onChanged} />
            {tools.length > 0 && (
                <ul className="plugins-tree__tools" aria-label={`${item.name}’s tools`}>
                    {tools.map((t) => <ToolRow key={t.id || 'tool'} item={item} tool={t} runHint={runHint} onOpenTool={onOpenTool} />)}
                </ul>
            )}
        </li>
    );
}

/** A plugin's tools alone, as the inspector's Tools band shows them. */
export function ToolRows({ item, tools, runHint, onOpenTool }) {
    return (
        <ul className="plugins-tree__tools is-alone" aria-label="Tools">
            {tools.map((t) => <ToolRow key={t.id || 'tool'} item={item} tool={t} runHint={runHint} onOpenTool={onOpenTool} />)}
        </ul>
    );
}

export default function PluginTree({
    sets, query = '', openId, loaded = true, emptyOf = (s) => s.empty, gate, groupId, runHint, heldId,
    onOpen, onOpenTool, onContinue, onChanged,
}) {
    return (
        <div className="plugins-tree">
            {sets.map((s) => (
                <TitledSet key={s.id} title={s.title} count={loaded ? s.list.length : undefined} empty={emptyOf(s)}>
                    {s.list.length > 0 && (
                        <ul className="plugins-tree__list" aria-label={s.title}>
                            {s.list.map((p) => (
                                <PluginBranch
                                    key={p.id} item={p} query={query} current={String(p.id) === String(openId)}
                                    gate={gate} groupId={groupId} runHint={runHint} heldId={heldId}
                                    onOpen={onOpen} onOpenTool={onOpenTool} onContinue={onContinue} onChanged={onChanged}
                                />
                            ))}
                        </ul>
                    )}
                </TitledSet>
            ))}
        </div>
    );
}
